/**
 * Unit Conversion Utilities for SKU Manager
 *
 * All internal storage uses canonical units:
 * - Length: millimeters (mm)
 * - Weight: grams (g)
 *
 * This utility converts between canonical and display units based on
 * organization preferences.
 */

// ============================================================================
// Types
// ============================================================================

export type LengthUnit = 'mm' | 'cm' | 'in' | 'ft';
export type WeightUnit = 'g' | 'kg' | 'oz' | 'lb';
export type UnitSystem = 'metric' | 'imperial';

export interface LengthPreference {
  unit: LengthUnit;
  precision: number;
}

export interface WeightPreference {
  unit: WeightUnit;
  precision: number;
}

export interface UnitPreferences {
  system: UnitSystem;
  length: LengthPreference;
  weight: WeightPreference;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Conversion factors TO canonical units (mm for length)
 * To convert FROM a unit to mm: value * LENGTH_TO_MM[unit]
 * To convert TO a unit from mm: value / LENGTH_TO_MM[unit]
 */
export const LENGTH_TO_MM: Record<LengthUnit, number> = {
  mm: 1,
  cm: 10,
  in: 25.4,
  ft: 304.8,
};

/**
 * Conversion factors TO canonical units (grams for weight)
 * To convert FROM a unit to grams: value * WEIGHT_TO_GRAMS[unit]
 * To convert TO a unit from grams: value / WEIGHT_TO_GRAMS[unit]
 */
export const WEIGHT_TO_GRAMS: Record<WeightUnit, number> = {
  g: 1,
  kg: 1000,
  oz: 28.3495,
  lb: 453.592,
};

/**
 * Human-readable labels for length units
 */
export const LENGTH_UNIT_LABELS: Record<LengthUnit, string> = {
  mm: 'Millimeters (mm)',
  cm: 'Centimeters (cm)',
  in: 'Inches (in)',
  ft: 'Feet (ft)',
};

/**
 * Human-readable labels for weight units
 */
export const WEIGHT_UNIT_LABELS: Record<WeightUnit, string> = {
  g: 'Grams (g)',
  kg: 'Kilograms (kg)',
  oz: 'Ounces (oz)',
  lb: 'Pounds (lb)',
};

/**
 * Short labels for display (unit symbols)
 */
export const LENGTH_UNIT_SHORT: Record<LengthUnit, string> = {
  mm: 'mm',
  cm: 'cm',
  in: 'in',
  ft: 'ft',
};

export const WEIGHT_UNIT_SHORT: Record<WeightUnit, string> = {
  g: 'g',
  kg: 'kg',
  oz: 'oz',
  lb: 'lb',
};

/**
 * Default preferences for metric system
 */
export const DEFAULT_METRIC_PREFERENCES: UnitPreferences = {
  system: 'metric',
  length: { unit: 'mm', precision: 0 },
  weight: { unit: 'g', precision: 0 },
};

/**
 * Default preferences for imperial system
 */
export const DEFAULT_IMPERIAL_PREFERENCES: UnitPreferences = {
  system: 'imperial',
  length: { unit: 'in', precision: 2 },
  weight: { unit: 'oz', precision: 1 },
};

// ============================================================================
// Conversion Functions
// ============================================================================

/**
 * Convert length from canonical (mm) to display unit
 * @param valueMm - Value in millimeters (canonical)
 * @param toUnit - Target display unit
 * @param precision - Number of decimal places (default: 2)
 * @returns Converted value or null if input is null/undefined
 */
export function convertLengthFromMm(
  valueMm: number | null | undefined,
  toUnit: LengthUnit,
  precision: number = 2,
): number | null {
  if (valueMm === null || valueMm === undefined) return null;
  const converted = valueMm / LENGTH_TO_MM[toUnit];
  return Number(converted.toFixed(precision));
}

/**
 * Convert length from display unit to canonical (mm)
 * @param value - Value in display unit
 * @param fromUnit - Source display unit
 * @returns Value in millimeters (rounded to integer) or null if input is null/undefined
 */
export function convertLengthToMm(
  value: number | null | undefined,
  fromUnit: LengthUnit,
): number | null {
  if (value === null || value === undefined) return null;
  return Math.round(value * LENGTH_TO_MM[fromUnit]);
}

/**
 * Convert weight from canonical (grams) to display unit
 * @param valueGrams - Value in grams (canonical)
 * @param toUnit - Target display unit
 * @param precision - Number of decimal places (default: 2)
 * @returns Converted value or null if input is null/undefined
 */
export function convertWeightFromGrams(
  valueGrams: number | null | undefined,
  toUnit: WeightUnit,
  precision: number = 2,
): number | null {
  if (valueGrams === null || valueGrams === undefined) return null;
  const converted = valueGrams / WEIGHT_TO_GRAMS[toUnit];
  return Number(converted.toFixed(precision));
}

/**
 * Convert weight from display unit to canonical (grams)
 * @param value - Value in display unit
 * @param fromUnit - Source display unit
 * @returns Value in grams (rounded to integer) or null if input is null/undefined
 */
export function convertWeightToGrams(
  value: number | null | undefined,
  fromUnit: WeightUnit,
): number | null {
  if (value === null || value === undefined) return null;
  return Math.round(value * WEIGHT_TO_GRAMS[fromUnit]);
}

/**
 * Decimal places needed, in a display unit, to represent ONE canonical unit (SKU-1876).
 *
 * A display unit larger than the canonical one needs decimals to show a stored value without
 * losing any of it: kg needs 3 (1 g = 0.001 kg), cm needs 1, inches need 2 (1 mm ≈ 0.04 in).
 *
 * This is a FLOOR, not the whole answer, and reading it as the whole answer was a bug — see
 * `decimalsIn`. It assumes the canonical value is a whole number of canonical units, which the
 * columns do not guarantee: `weight` is `Decimal(8,3)` grams and `length`/`width`/`height` are
 * `Decimal(8,2)` millimetres (SKU-1895 Phase B).
 *
 * This is deliberately NOT the organisation's display precision. That preference governs
 * read-only formatting (`formatWeight`, `formatLength`); an editable field must show all of
 * what is stored, or the operator is asked to confirm a number that is not the one on file.
 */
export function getCanonicalDecimals(factorToCanonical: number): number {
  return Math.max(0, Math.ceil(Math.log10(factorToCanonical)));
}

/**
 * Decimal places the value ITSELF carries (SKU-1899).
 *
 * The other half of the "keep every unit that is stored" promise. `getCanonicalDecimals` only
 * accounts for the display unit being coarser than the canonical one; it cannot know that the
 * stored value has decimals of its own. For an `mm` display of an `mm`-canonical value it
 * returns 0, so `10.25` mm was shown as `10` and `184.272` g as `184` — a field asserting a
 * stored number that is not the one on file, which is the precision-floor rule from PR #3183
 * and Critical Rule 2.
 *
 * Capped at 6 so a float artefact (`0.1 + 0.2` is `0.30000000000000004`) cannot drive a
 * 17-digit `toFixed`.
 */
function decimalsIn(value: number): number {
  if (!Number.isFinite(value)) return 0;
  // Exponent form ("1e-7") has no '.' to measure; such a value is below anything we store.
  const text = value.toString();
  const dot = text.indexOf('.');
  if (dot === -1 || text.includes('e') || text.includes('E')) return 0;
  return Math.min(6, text.length - dot - 1);
}

/**
 * Decimals a display value needs to round-trip back to `canonical` unchanged (SKU-1899).
 *
 * The two contributions ADD; they are not a maximum. `getCanonicalDecimals` covers the decimal
 * shift from dividing by the unit's factor (mm → cm moves the point one place), and `decimalsIn`
 * covers the decimals the canonical value already carried. 10.25 mm in cm is 1.025 — the unit
 * contributes 1 place, the value 2, and taking the larger of them would land on 1.02 and lose
 * the 5 thousandths. Getting this wrong is invisible in the common case, because production
 * values were whole canonical units and `decimalsIn` was then 0.
 *
 * Capped at 8 total, which is past every column's resolution (`Decimal(8,3)` grams,
 * `Decimal(8,2)` millimetres) and well inside `toFixed`'s range.
 */
function displayDecimals(canonical: number, factorToCanonical: number): number {
  return Math.min(8, getCanonicalDecimals(factorToCanonical) + decimalsIn(canonical));
}

/**
 * Convert grams to a display unit, keeping every gram that is stored.
 *
 * "Exact" means the returned number round-trips: it keeps the decimals the display unit needs
 * AND the decimals the value already had (see `displayDecimals`). Changed for the weight half as
 * well as the length half deliberately — `weight` is `Decimal(8,3)`, so the converted OUNCES
 * rows on production genuinely hold three decimals, and fixing only the length half would have
 * certified the same bug next door.
 *
 * @returns Converted value or null if input is null/undefined
 */
export function convertWeightFromGramsExact(
  valueGrams: number | null | undefined,
  toUnit: WeightUnit,
): number | null {
  if (valueGrams === null || valueGrams === undefined) return null;
  const factor = WEIGHT_TO_GRAMS[toUnit];
  const converted = valueGrams / factor;
  return Number(converted.toFixed(displayDecimals(valueGrams, factor)));
}

/**
 * Convert mm to a display unit, keeping every millimetre that is stored.
 *
 * See `convertWeightFromGramsExact`. This affects READ/DISPLAY only — what can be TYPED is
 * still whole millimetres, because `convertLengthToMm` rounds. The two are deliberately
 * different: restricting new input is a limit, whereas re-deriving a stored `10.25` as `10`
 * would show the operator a number that is not on file and make it real on the next edit.
 *
 * @returns Converted value or null if input is null/undefined
 */
export function convertLengthFromMmExact(
  valueMm: number | null | undefined,
  toUnit: LengthUnit,
): number | null {
  if (valueMm === null || valueMm === undefined) return null;
  const factor = LENGTH_TO_MM[toUnit];
  const converted = valueMm / factor;
  return Number(converted.toFixed(displayDecimals(valueMm, factor)));
}

// ============================================================================
// Formatting Functions
// ============================================================================

/**
 * Format length value with unit for display
 * Uses smart defaults for precision: mm=0, cm=1, in=2, ft=3 (see getDefaultLengthPrecision)
 * @param valueMm - Value in millimeters (canonical)
 * @param preferences - Unit preferences
 * @returns Formatted string like "12.5 in" or "N/A" if null
 */
export function formatLength(
  valueMm: number | null | undefined,
  preferences: UnitPreferences,
): string {
  if (valueMm === null || valueMm === undefined) return 'N/A';
  const { unit } = preferences.length;
  const precision = getDefaultLengthPrecision(unit);
  const converted = convertLengthFromMm(valueMm, unit, precision);
  if (converted === null) return 'N/A';
  return `${converted} ${LENGTH_UNIT_SHORT[unit]}`;
}

/**
 * Format weight value with unit for display
 * @param valueGrams - Value in grams (canonical)
 * @param preferences - Unit preferences
 * @returns Formatted string like "8.2 oz" or "N/A" if null
 */
export function formatWeight(
  valueGrams: number | null | undefined,
  preferences: UnitPreferences,
): string {
  if (valueGrams === null || valueGrams === undefined) return 'N/A';
  const { unit, precision } = preferences.weight;
  const converted = convertWeightFromGrams(valueGrams, unit, precision);
  if (converted === null) return 'N/A';
  return `${converted} ${WEIGHT_UNIT_SHORT[unit]}`;
}

/**
 * Format dimensions (L x W x H) with unit for display
 * Uses smart defaults for precision: mm=0, cm=1, in=2, ft=3 (see getDefaultLengthPrecision)
 * @param lengthMm - Length in millimeters
 * @param widthMm - Width in millimeters
 * @param heightMm - Height in millimeters
 * @param preferences - Unit preferences
 * @returns Formatted string like "6 × 4 × 2 in" or "N/A" if any dimension is null
 */
export function formatDimensions(
  lengthMm: number | null | undefined,
  widthMm: number | null | undefined,
  heightMm: number | null | undefined,
  preferences: UnitPreferences,
): string {
  if (lengthMm == null || widthMm == null || heightMm == null) return 'N/A';

  const { unit } = preferences.length;
  const precision = getDefaultLengthPrecision(unit);
  const l = convertLengthFromMm(lengthMm, unit, precision);
  const w = convertLengthFromMm(widthMm, unit, precision);
  const h = convertLengthFromMm(heightMm, unit, precision);

  if (l === null || w === null || h === null) return 'N/A';
  return `${l} × ${w} × ${h} ${LENGTH_UNIT_SHORT[unit]}`;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Partial unit preferences from domain settings
 */
export interface PartialUnitSettings {
  system?: UnitSystem;
  length?: Partial<LengthPreference>;
  weight?: Partial<WeightPreference>;
}

/**
 * Get complete unit preferences from domain settings with defaults
 * @param domainSettings - Domain settings object (may have units property)
 * @returns Complete UnitPreferences with defaults applied
 */
export function getUnitPreferences(
  domainSettings?: { units?: PartialUnitSettings } | null,
): UnitPreferences {
  const units = domainSettings?.units;
  const system = units?.system ?? 'metric';
  const defaults =
    system === 'imperial' ? DEFAULT_IMPERIAL_PREFERENCES : DEFAULT_METRIC_PREFERENCES;

  return {
    system,
    length: {
      unit: units?.length?.unit ?? defaults.length.unit,
      precision: units?.length?.precision ?? defaults.length.precision,
    },
    weight: {
      unit: units?.weight?.unit ?? defaults.weight.unit,
      precision: units?.weight?.precision ?? defaults.weight.precision,
    },
  };
}

/**
 * Get the default precision for a length unit (mm=0, cm=1, in=2, ft=3).
 *
 * Derived from `getCanonicalDecimals`, not hand-maintained per unit: the number of decimals
 * a display unit needs is a property of its conversion factor, so there is nothing to keep
 * in sync. The previous hand-written table gave cm 0 decimals, which rendered a stored
 * 125 mm as "13 cm" — a packing slip showing a number that is neither what was measured nor
 * what the carrier was quoted (SKU-1877).
 *
 * This is the floor, not a minimum width: values are formatted as numbers, so a dimension
 * that is exact in the display unit still reads "13 cm", never "13.0 cm".
 */
export function getDefaultLengthPrecision(unit: LengthUnit): number {
  return getCanonicalDecimals(LENGTH_TO_MM[unit]);
}
